import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  list: {
    maxHeight: 320,
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  thumb: {
    width: 44,
    height: 44,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid rgba(0,0,0,0.12)",
    marginRight: theme.spacing(1)
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
    opacity: 0.7
  },
  modeGroup: {
    marginBottom: theme.spacing(1)
  }
}));

const formatPrice = (cents, currency) => {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
    return "";
  }
  const value = Number(cents) / 100;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "BRL"
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

/**
 * Seletor de produtos do catálogo para envio no ticket.
 *
 * As opções de envio dependem do canal: o Baileys só manda um produto por vez,
 * enquanto a Cloud API também monta lista e catálogo completo. Em vez de deixar
 * o atendente descobrir isso pelo erro, o que o canal não suporta vem
 * desabilitado com o motivo no tooltip — as capacidades chegam de
 * /catalog/capabilities.
 */
const CatalogProductPickerModal = ({ open, onClose, ticketId, whatsappId }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState("single");
  const [bodyText, setBodyText] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [capabilities, setCapabilities] = useState(null);

  const canSendList = Boolean(capabilities?.canSendProductList);
  const canSendCatalog = Boolean(capabilities?.canSendCatalog);

  useEffect(() => {
    if (!open) return;
    setSearchParam("");
    setSelectedIds([]);
    setMode("single");
    setBodyText("");
    setHeaderText("");
    setFooterText("");
  }, [open]);

  useEffect(() => {
    if (!open || !whatsappId) return;

    const fetchCapabilities = async () => {
      try {
        const { data } = await api.get("/catalog/capabilities", {
          params: { whatsappId }
        });
        setCapabilities(data);
      } catch (err) {
        setCapabilities(null);
      }
    };

    fetchCapabilities();
  }, [open, whatsappId]);

  const fetchProducts = useCallback(async () => {
    if (!open || !whatsappId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/catalog/products", {
        params: { searchParam, whatsappId, onlyVisible: true, pageNumber: 1 }
      });
      setProducts(data.records || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [open, whatsappId, searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchProducts]);

  const handleToggle = productId => {
    setSelectedIds(prev => {
      if (mode === "single") {
        // Em produto único a seleção é exclusiva: clicar em outro troca.
        return prev.includes(productId) ? [] : [productId];
      }
      return prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId];
    });
  };

  const handleModeChange = event => {
    const next = event.target.value;
    setMode(next);
    // Trocar para produto único descarta a seleção múltipla, senão o envio
    // mandaria um produto arbitrário da lista.
    if (next === "single" && selectedIds.length > 1) {
      setSelectedIds(selectedIds.slice(0, 1));
    }
  };

  const handleSend = async () => {
    if (mode !== "catalog" && selectedIds.length === 0) {
      toast.error(i18n.t("catalog.picker.selectOne"));
      return;
    }

    setSending(true);
    try {
      await api.post("/catalog/send", {
        ticketId,
        mode,
        productIds: selectedIds,
        bodyText: bodyText || undefined,
        headerText: headerText || undefined,
        footerText: footerText || undefined
      });
      toast.success(i18n.t("catalog.picker.sent"));
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSending(false);
  };

  /** Opção desabilitada explica o porquê, em vez de só ficar cinza. */
  const modeOption = (value, label, enabled, reason) => {
    const control = (
      <FormControlLabel
        value={value}
        control={<Radio color="primary" />}
        label={label}
        disabled={!enabled}
      />
    );
    return enabled || !reason ? (
      control
    ) : (
      <Tooltip title={reason}>
        <span>{control}</span>
      </Tooltip>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{i18n.t("catalog.picker.title")}</DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset" className={classes.modeGroup}>
          <FormLabel component="legend">{i18n.t("catalog.picker.mode")}</FormLabel>
          <RadioGroup row value={mode} onChange={handleModeChange}>
            {modeOption(
              "single",
              i18n.t("catalog.picker.modeSingle"),
              true,
              null
            )}
            {modeOption(
              "list",
              i18n.t("catalog.picker.modeList"),
              canSendList,
              capabilities?.reasons?.canSendProductList
            )}
            {modeOption(
              "catalog",
              i18n.t("catalog.picker.modeCatalog"),
              canSendCatalog,
              capabilities?.reasons?.canSendCatalog
            )}
          </RadioGroup>
        </FormControl>

        {mode !== "catalog" && (
          <>
            <TextField
              placeholder={i18n.t("catalog.picker.searchPlaceholder")}
              type="search"
              value={searchParam}
              fullWidth
              variant="outlined"
              margin="dense"
              onChange={e => setSearchParam(e.target.value.toLowerCase())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                )
              }}
            />

            {loading && (
              <div className={classes.empty}>
                <CircularProgress size={24} />
              </div>
            )}

            {!loading && products.length === 0 && (
              <Typography variant="body2" className={classes.empty}>
                {i18n.t("catalog.picker.empty")}
              </Typography>
            )}

            {!loading && products.length > 0 && (
              <List className={classes.list} dense>
                {products.map(product => (
                  <ListItem
                    key={product.id}
                    button
                    onClick={() => handleToggle(product.id)}
                  >
                    <Checkbox
                      edge="start"
                      color="primary"
                      checked={selectedIds.includes(product.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    {product.imageUrl && (
                      <img
                        className={classes.thumb}
                        src={product.imageUrl}
                        alt={product.name}
                        onError={e => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <ListItemText
                      primary={product.name}
                      secondary={[
                        formatPrice(product.priceCents, product.currency),
                        product.retailerId
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {mode === "list" && (
          <TextField
            label={i18n.t("catalog.picker.header")}
            value={headerText}
            fullWidth
            variant="outlined"
            margin="dense"
            onChange={e => setHeaderText(e.target.value)}
          />
        )}

        <TextField
          label={i18n.t("catalog.picker.message")}
          value={bodyText}
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          margin="dense"
          onChange={e => setBodyText(e.target.value)}
        />

        <TextField
          label={i18n.t("catalog.picker.footer")}
          value={footerText}
          fullWidth
          variant="outlined"
          margin="dense"
          onChange={e => setFooterText(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          {i18n.t("catalog.picker.cancel")}
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={sending}
          startIcon={sending ? <CircularProgress size={16} /> : null}
        >
          {i18n.t("catalog.picker.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogProductPickerModal;
